import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { TFunction } from "i18next";

import { ItemSet } from "@/components/elements";
import { formatCurrency, formatNumber } from "@/utils/formatEuro";
import { isExcludedTransactionRecord } from "@/utils/transactionRecord";

import type { AuctionCarTransaction } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { PaginatedList } from "@/types/entity";
import type { MarketCar } from "@/types/market";

dayjs.extend(isSameOrAfter);

export function getMarketHeadlinesData(
  t: TFunction<"translation", undefined>,
  fv: (path: string) => string,
  data: MarketCar | undefined,
  currency: Currency
) {
  return [
    {
      title: t("car.assetclassic_valuation"),
      titleTooltip: t("tooltips.carburateValueGuide"),
      items: [
        {
          label: t("car.conditions.fair"),
          value: fv("market_prices.valuation_by_condition.fair"),
          infoIcon: t("tooltips.fair"),
        },
        {
          label: t("car.conditions.good"),
          value: fv("market_prices.valuation_by_condition.good"),
          infoIcon: t("tooltips.good"),
        },
        {
          label: t("car.conditions.excellent"),
          value: fv("market_prices.valuation_by_condition.excellent"),
          infoIcon: t("tooltips.excellent"),
        },
        {
          label: t("car.conditions.superb"),
          value: fv("market_prices.valuation_by_condition.superb"),
          infoIcon: t("tooltips.superb"),
        },
      ],
    },
    data?.value_adjustment && {
      cols: 1,
      items: [
        {
          label: t("car.value_adjustment"),
          infoIcon: t("tooltips.valueAdjustment"),
          value: data.value_adjustment.replaceAll("EUR", currency.code),
          truncate: false,
        },
      ],
    },
    {
      title: t("car.hammer_price_3y"),
      items: [
        { label: t("car.conditions.lowest"), value: fv("market_prices.hammer_price_3y.lowest") },
        { label: t("car.conditions.average"), value: fv("market_prices.hammer_price_3y.average") },
        { label: t("car.conditions.highest"), value: fv("market_prices.hammer_price_3y.highest") },
        {
          label: t("car.conditions.net-to-seller"),
          value: fv("market_prices.hammer_price_3y.net_to_seller_average"),
          infoIcon: t("tooltips.netToSeller"),
        },
      ],
    },
    {
      title: t("car.hammer_price_1y"),
      items: [
        { label: t("car.conditions.lowest"), value: fv("market_prices.hammer_price_1y.lowest") },
        { label: t("car.conditions.average"), value: fv("market_prices.hammer_price_1y.average") },
        { label: t("car.conditions.highest"), value: fv("market_prices.hammer_price_1y.highest") },
        {
          label: t("car.conditions.net-to-seller"),
          value: fv("market_prices.hammer_price_1y.net_to_seller_average"),
        },
      ],
    },
  ].filter(Boolean) as ItemSet[];
}

export function getAuctionsData(
  t: TFunction<"translation", undefined>,
  source: "auctions" | "ads",
  transactions?: PaginatedList<AuctionCarTransaction>,
  currency?: Currency,
  years = 3
) {
  const totalTransactions = transactions?.total ?? 0;

  const soldStatuses = new Set<AuctionCarTransaction["result"]>([
    "sold_at_auction",
    "sold_post_auction",
    "sold",
  ]);

  const since = dayjs().subtract(years, "year").startOf("day");

  const results = (transactions?.results ?? []).filter((tr) => !isExcludedTransactionRecord(tr));

  // Sell-through rate: only auctions in the last `years`
  const auctionsLastN = results.filter(
    (tr) => tr.type === "auction" && dayjs(tr.date).isSameOrAfter(since)
  );
  const soldAuctionsLastN = auctionsLastN.filter((tr) => soldStatuses.has(tr.result)).length;
  const sellThroughRateRaw =
    auctionsLastN.length > 0 ? Math.round((soldAuctionsLastN / auctionsLastN.length) * 100) : 0;

  // Volume traded (last `years`): auctions (sold) + ads (listed)
  const auctionsVolume = results
    .filter(
      (tr) =>
        tr.type === "auction" && soldStatuses.has(tr.result) && dayjs(tr.date).isSameOrAfter(since)
    )
    .reduce((acc, cur) => acc + (cur.hammer_price ?? 0), 0);

  const adsVolume = results
    .filter((tr) => tr.type === "ad" && dayjs(tr.date).isSameOrAfter(since))
    .reduce((acc, cur) => acc + (cur.listing_price ?? cur.price ?? 0), 0);

  const volumeTradedRaw = auctionsVolume + adsVolume;

  // Total value (all time): auctions (sold) + ads (listed) without timeframe
  const auctionsVolumeAll = results
    .filter((tr) => tr.type === "auction" && soldStatuses.has(tr.result))
    .reduce((acc, cur) => acc + (cur.hammer_price ?? 0), 0);

  const adsVolumeAll = results
    .filter((tr) => tr.type === "ad")
    .reduce((acc, cur) => acc + (cur.listing_price ?? cur.price ?? 0), 0);

  const totalValueRaw = auctionsVolumeAll + adsVolumeAll;

  const sellThroughRate = `${sellThroughRateRaw}%`;
  const volumeTraded = currency
    ? formatCurrency(volumeTradedRaw, currency.code)
    : formatCurrency(volumeTradedRaw);

  return [
    {
      title:
        source === "auctions"
          ? t("components.Auctions.event_analysis")
          : t("components.Auctions.ad_listing_event_analysis"),
      items: [
        {
          label: t("components.Auctions.number_of_transactions"),
          value: formatNumber(totalTransactions, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        },
        {
          label: t("components.Auctions.total_value"),
          value: currency
            ? formatCurrency(totalValueRaw, currency.code)
            : formatCurrency(totalValueRaw),
        },
        { label: t("components.Auctions.volume_traded"), value: volumeTraded },

        { label: t("components.Auctions.sell_through_rate"), value: sellThroughRate },
      ],
    },
  ];
}

// Formats a numeric value into a currency string. Returns "-" if the value is null or undefined.
export function formatValue(value: number | null | undefined, currency: Currency) {
  return value ? formatCurrency(value, currency.code) : "-";
}

// Function to retrieve a nested property from an object using a dot-separated path string
export const getNested = (obj: any, path: string) =>
  path.split(".").reduce((acc, key) => acc?.[key], obj);

// Function to format a nested numeric value from the `data` object
export function formatNestedValue(data: MarketCar | undefined, currency: Currency) {
  return (path: string) => formatValue(getNested(data, path) ?? 0, currency);
}
